import { ShieldCheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { NavLink, useLocation } from "react-router-dom";

import { PLATFORM_NAV, type PlatformNavItem } from "../navigation/platformNav";

import { Button } from "@/components/Button";
import {
  SIDEBAR_HOVER_BG,
  SIDEBAR_ICON_ACTIVE,
  SIDEBAR_ICON_INACTIVE,
  SIDEBAR_ITEM_ACTIVE,
  SIDEBAR_ITEM_BASE,
  SIDEBAR_ITEM_COLLAPSED,
  SIDEBAR_ITEM_EXPANDED,
  SIDEBAR_MUTED_TEXT,
  SIDEBAR_TEXT_INACTIVE,
} from "@/components/layouts/Sidebar/sidebarClassNames";

/**
 * `/platform` is an index route, so `NavLink end` would be the obvious way to keep Overview from
 * matching every child path. It is not enough on its own: `/platform/tenants/:id` must light up
 * Tenants, so each item needs prefix matching *except* Overview, which needs exact matching.
 */
function isItemActive(item: PlatformNavItem, pathname: string): boolean {
  if (item.href === "/platform") return pathname === "/platform";
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
}

function PlatformNavLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: PlatformNavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  const active = isItemActive(item, pathname);
  const Icon = item.icon;

  return (
    <NavLink
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      title={collapsed ? item.name : undefined}
      className={clsx(
        SIDEBAR_ITEM_BASE,
        collapsed ? SIDEBAR_ITEM_COLLAPSED : SIDEBAR_ITEM_EXPANDED,
        "text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
        active ? SIDEBAR_ITEM_ACTIVE : clsx(SIDEBAR_TEXT_INACTIVE, SIDEBAR_HOVER_BG),
        !item.available && !active && "opacity-60",
      )}
    >
      {active && !collapsed && (
        <span
          aria-hidden="true"
          className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-blue-600 dark:bg-blue-400"
        />
      )}
      <Icon
        className={clsx(
          "h-5 w-5 shrink-0",
          active ? SIDEBAR_ICON_ACTIVE : SIDEBAR_ICON_INACTIVE,
        )}
        aria-hidden="true"
      />
      {!collapsed && (
        <>
          <span className="truncate">{item.name}</span>
          {!item.available && (
            <span
              className={clsx(
                "ml-auto rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
              )}
            >
              Soon
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function PlatformBranding({ collapsed }: { collapsed: boolean }) {
  return (
    <div
      className={clsx(
        "flex items-center gap-3 border-b border-slate-200 px-3 py-4 dark:border-slate-800",
        collapsed && "justify-center px-2",
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm">
        <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
      </span>
      {!collapsed && (
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-slate-900 dark:text-white">
            Trovix Campus
          </span>
          <span className={clsx("block truncate text-xs", SIDEBAR_MUTED_TEXT)}>
            SMS SaaS Platform
          </span>
        </span>
      )}
    </div>
  );
}

function PlatformNavList({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  return (
    <nav
      aria-label="Platform"
      className={clsx("space-y-5", collapsed ? "px-2 py-3" : "px-3 py-4")}
    >
      {PLATFORM_NAV.map((section) => (
        <div key={section.id} className="space-y-1">
          {!collapsed && (
            <p
              className={clsx(
                "px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider",
                SIDEBAR_MUTED_TEXT,
              )}
            >
              {section.title}
            </p>
          )}
          {section.items.map((item) => (
            <PlatformNavLink
              key={item.id}
              item={item}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
    </nav>
  );
}

interface PlatformSidebarProps {
  collapsed: boolean;
  isMobileMenuOpen: boolean;
  onMobileMenuClose: () => void;
}

export function PlatformSidebar({
  collapsed,
  isMobileMenuOpen,
  onMobileMenuClose,
}: PlatformSidebarProps) {
  return (
    <>
      {/* Desktop. Offsets below the support-session banner via the same CSS var the tenant
          sidebar uses, so a platform owner mid-session sees both chrome pieces stacked. */}
      <aside
        className={clsx(
          "fixed left-0 z-40 hidden flex-col lg:flex",
          "top-[var(--platform-banner-h,0px)] h-[calc(100vh-var(--platform-banner-h,0px))]",
          collapsed ? "w-20" : "w-64",
          "border-r border-slate-200 bg-white transition-all duration-300",
          "dark:border-slate-800 dark:bg-slate-950",
        )}
      >
        <PlatformBranding collapsed={collapsed} />
        <div className="app-themed-scroll flex-1 overflow-y-auto">
          <PlatformNavList collapsed={collapsed} />
        </div>
      </aside>

      {/* Mobile drawer */}
      <aside
        className={clsx(
          "fixed bottom-0 left-0 z-50 w-72 flex-col lg:hidden",
          "top-[var(--platform-banner-h,0px)]",
          "border-r border-slate-200 bg-white transition-transform duration-300",
          "dark:border-slate-800 dark:bg-slate-950",
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full",
        )}
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="relative">
          <PlatformBranding collapsed={false} />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onMobileMenuClose}
            aria-label="Close menu"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2"
          >
            <XMarkIcon className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </Button>
        </div>
        <div className="app-themed-scroll flex-1 overflow-y-auto">
          <PlatformNavList collapsed={false} onNavigate={onMobileMenuClose} />
        </div>
      </aside>
    </>
  );
}
